/**
 * AdminLayout - Layout component que envuelve las páginas con AdminTopNavigationBar
 * Integra Zustand stores para gestión de estado global
 */

import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import AdminTopNavigationBar from "../Header/AdminTopNavigationBar";
import CommandPalette from "../CommandPalette/CommandPalette";
import AppLauncher from "../Navigation/AppLauncher";
import SettingsPanel from "../Navigation/SettingsPanel";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import { useNotificationStore } from "../../stores/notificationStore";
import { getThemeById } from "../../data/themeColors";

const AdminLayout = ({ children }) => {
  const history = useHistory();
  
  // Zustand stores
  const { apps, settings, accessApp, setView } = useAppRegistryStore();
  const { notifications } = useNotificationStore();
  
  // Aplicar tema oscuro al layout
  const isDarkMode = settings?.currentTheme === "dark";
  
  // Aplicar tema de color al cargar (igual que NewLnF - simple y directo)
  useEffect(() => {
    const selectedTheme = settings?.selectedTheme || "default";
    const themeColors = getThemeById(selectedTheme);

    // Aplicar variables y stylesheet dinámico (como en NewLnF)
    if (themeColors && themeColors.colors) {
      const root = document.documentElement;
      const body = document.body;

      // Variables CSS base
      Object.entries(themeColors.colors).forEach(([key, value]) => {
        const cssVar = key.replace(/([A-Z])/g, "-$1").toLowerCase();
        root.style.setProperty(`--${cssVar}`, value);
      });

      const bg = isDarkMode ? '#121212' : themeColors.colors.background || '#ffffff'
      const fg = isDarkMode ? '#e0e0e0' : themeColors.colors.foreground || '#1a1a1a'
      const cardBg = isDarkMode ? '#1e1e1e' : themeColors.colors.card || '#ffffff'

      // Variables usadas por index.css
      root.style.setProperty('--bg-primary', bg);
      root.style.setProperty('--text-primary', fg);
      root.style.setProperty('--card-bg', cardBg);

      // Fondo y texto del body controlados por variables
      if (body) {
        body.style.removeProperty('background-color');
        body.style.removeProperty('color');
      }

      {
        // Crear/actualizar stylesheet SOLO para body, sin interferir con ThemeSelector
        let styleSheet = document.getElementById('layout-theme-styles');
        const darkModeStyleSheet = document.getElementById('dark-mode-styles');
        if (!styleSheet) {
          styleSheet = document.createElement('style');
          styleSheet.id = 'layout-theme-styles';
          if (darkModeStyleSheet) {
            document.head.insertBefore(styleSheet, darkModeStyleSheet);
          } else {
            document.head.appendChild(styleSheet);
          }
        } else {
          styleSheet.remove();
          if (darkModeStyleSheet) {
            document.head.insertBefore(styleSheet, darkModeStyleSheet);
          } else {
            document.head.appendChild(styleSheet);
          }
        }

        styleSheet.textContent = `
          body,
          html body {
            background-color: ${bg} !important;
            color: ${fg} !important;
          }
        `;

        const applyDirectly = () => {
          if (document.body) {
            document.body.style.setProperty('background-color', bg, 'important');
            document.body.style.setProperty('color', fg, 'important');
          }
        };
        applyDirectly();
        setTimeout(applyDirectly, 50);
        setTimeout(applyDirectly, 150);
        setTimeout(applyDirectly, 300);
      }
    }
  }, [settings?.selectedTheme, isDarkMode]);
  
  // Estados locales
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [appLauncherOpen, setAppLauncherOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // Obtener usuario de sessionStorage
  const getUserInfo = () => {
    const email = window.sessionStorage.getItem("User") || "admin@example.com";
    // Extraer el nombre del email (parte antes del @) y capitalizarlo
    const nameFromEmail = email.split("@")[0]
      .split(/[._-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    
    return {
      name: nameFromEmail || "Admin User",
      email: email,
      avatar: null,
    };
  };
  
  const user = getUserInfo();

  // Obtener apps fijadas desde el store o config
  const pinnedApps = apps.filter((app) => app.pinned && !app.hidden).sort((a, b) => (a.order || 0) - (b.order || 0));

  // Detectar tamaño de pantalla para sincronizar layout con la barra
  const [isLargeScreen, setIsLargeScreen] = useState(true);
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getActualLayout = () => {
    if (settings.layoutStyle === "auto-detect") {
      return isLargeScreen ? "horizontal" : "vertical";
    }
    return settings.layoutStyle;
  };
  const actualLayout = getActualLayout();

  // Handlers
  const handleAppClick = (app) => {
    accessApp(app.id);
    if (app.path) {
      history.push(app.path);
    }
  };

  const handleSearchClick = () => {
    setCommandPaletteOpen(true);
  };

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleAppLauncherClick = () => {
    setAppLauncherOpen(true);
  };

  const handleHomeDashboard = () => {
    setView("dashboard");
    history.push("/");
    // TODO: Mostrar Dashboard cuando esté disponible
  };

  const handleLogout = () => {
    window.sessionStorage.clear();
    history.push("/");
  };

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // CommandPalette: Ctrl+K / Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen(true);
        return;
      }

      // AppLauncher: Windows/Alt + Space
      // Usamos altKey (Alt) o metaKey (Windows/Cmd) + Space
      // Nota: En algunos navegadores, Windows+Space puede ser interceptado por el sistema
      // Por eso también soportamos Alt+Space como alternativa
      if ((e.altKey || e.metaKey) && e.key === " ") {
        // Evitar que se abra el menú de Windows si está presionada la tecla Windows
        // Solo procesar si no hay otros modificadores (Ctrl, Shift)
        if (!e.ctrlKey && !e.shiftKey) {
          e.preventDefault();
          setAppLauncherOpen(true);
        }
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <AdminTopNavigationBar
        pinnedApps={pinnedApps}
        onAppClick={handleAppClick}
        onSearchClick={handleSearchClick}
        onSettingsClick={handleSettingsClick}
        onAppLauncherClick={handleAppLauncherClick}
        onHomeDashboard={handleHomeDashboard}
        settings={settings}
        notifications={notifications}
        user={user}
        onLogout={handleLogout}
      />
      
      {/* Contenido principal con margen para la barra de navegación */}
      <main
        className={""}
        style={{
          marginTop: actualLayout === "vertical" ? 0 : "64px",
          marginLeft: actualLayout === "vertical" ? "64px" : 0,
          minHeight: "calc(100vh - 64px)",
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          transition: "background-color 0.3s ease, color 0.3s ease"
        }}
      >
        {children}
      </main>
      
      {/* CommandPalette */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
      
      {/* AppLauncher */}
      <AppLauncher
        isOpen={appLauncherOpen}
        onClose={() => setAppLauncherOpen(false)}
      />
      
      {/* SettingsPanel */}
      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
};

export default AdminLayout;

